import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError, money } from "@/lib/api";
import TransactionsTable from "@/components/dashboard/TransactionsTable";

export default function ClientDetailDialog({ clientId, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [adj, setAdj] = useState({ amount: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/admin/clients/${clientId}`).then(({ data }) => setData(data)).catch(() => {}), [clientId]);
  useEffect(() => { if (clientId) { setData(null); load(); } }, [clientId, load]);

  const adjust = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/clients/${clientId}/adjust`, { amount: Number(adj.amount), note: adj.note || "Manual adjustment" });
      toast.success("Balance adjusted");
      setAdj({ amount: "", note: "" });
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const p = data?.portfolio;
  return (
    <Dialog open={!!clientId} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-line bg-panel sm:max-w-3xl" data-testid="client-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{data?.client.name || "Client"}</DialogTitle>
          <DialogDescription>{data?.client.email}</DialogDescription>
        </DialogHeader>
        {p && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              {[["Cash", p.cash_balance, "client-detail-cash"], ["Holdings", p.holdings_value, "client-detail-holdings"], ["Total", p.total_value, "client-detail-total"]].map(([l, v, id]) => (
                <div key={l} className="rounded-xl border border-line bg-obsidian/50 p-4"><p className="eyebrow">{l}</p><p className="mt-2 font-mono text-lg text-white" data-testid={id}>{money(v)}</p></div>
              ))}
            </div>
            <div>
              <p className="eyebrow mb-3">Positions</p>
              {p.holdings.length ? (
                <ul className="space-y-2 text-sm">
                  {p.holdings.map((h) => (
                    <li key={h.id} className="flex justify-between rounded-lg border border-line/60 px-4 py-2">
                      <span className="text-slate-300">{h.label} <span className="text-slate-500">× {h.quantity}</span></span>
                      <span className="font-mono text-white">{money(h.market_value)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-slate-500">No positions.</p>}
            </div>
            <form onSubmit={adjust} className="grid gap-3 rounded-xl border border-line p-4 sm:grid-cols-[1fr_2fr_auto] sm:items-end">
              <div className="space-y-2"><Label htmlFor="adj-amount">Adjust cash (±)</Label>
                <Input id="adj-amount" type="number" step="0.01" required value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} className="field font-mono" data-testid="client-adjust-amount-input" /></div>
              <div className="space-y-2"><Label htmlFor="adj-note">Note</Label>
                <Input id="adj-note" value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} className="field" data-testid="client-adjust-note-input" /></div>
              <button type="submit" disabled={busy} className="btn-primary !py-2.5" data-testid="client-adjust-submit-button">Apply</button>
            </form>
            <div><p className="eyebrow mb-3">Transactions</p><TransactionsTable items={data.transactions} testId="client-detail-transactions" /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
